import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { ArrowUpRight, X } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { AnchorLink } from '@/components/generals/anchor-link'
import { DomeMark } from '@/components/generals/dome-mark'
import { useLenis } from '@/components/generals/smooth-scroll'
import { contact, navigation } from '@/constants/site'

interface SiteMenuProps {
    open: boolean
    onClose: () => void
}

const EASE = [0.83, 0, 0.17, 1] as const
const EASE_OUT = [0.16, 1, 0.3, 1] as const

export const SiteMenu: FC<SiteMenuProps> = ({ open, onClose }): ReactNode => {
    const panelRef = useRef<HTMLDivElement>(null)
    const lenis = useLenis()
    const reduced = useReducedMotion()

    useEffect(() => {
        if (!open) return

        const previous = document.activeElement as HTMLElement | null
        const root = document.documentElement

        if (lenis) lenis.stop()
        else root.style.overflow = 'hidden'

        const focusTimer = window.setTimeout(() => {
            panelRef.current?.querySelector<HTMLElement>('[data-menu-first]')?.focus({ preventScroll: true })
        }, 60)

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose()
                return
            }

            if (event.key !== 'Tab' || !panelRef.current) return

            const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'))
            const first = focusable[0]
            const last = focusable[focusable.length - 1]

            if (!first || !last) return

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }

        document.addEventListener('keydown', onKeyDown)

        return () => {
            window.clearTimeout(focusTimer)
            document.removeEventListener('keydown', onKeyDown)

            if (lenis) lenis.start()
            else root.style.overflow = ''

            previous?.focus?.({ preventScroll: true })
        }
    }, [open, lenis, onClose])

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key='site-menu'
                    id='site-menu'
                    ref={panelRef}
                    role='dialog'
                    aria-modal='true'
                    aria-label='Menu'
                    data-tone-scope='gold'
                    className='fixed inset-0 z-[60] flex flex-col overflow-y-auto bg-tone text-tone-fg'
                    data-lenis-prevent
                    initial={{ clipPath: 'inset(100% 0% 0% 0% round 50vw 50vw 0vw 0vw)' }}
                    animate={{ clipPath: 'inset(0% 0% 0% 0% round 0vw 0vw 0vw 0vw)' }}
                    exit={{ clipPath: 'inset(0% 0% 100% 0% round 0vw 0vw 50vw 50vw)' }}
                    transition={{ duration: reduced ? 0 : 0.95, ease: EASE }}
                >
                    <div className='flex h-16 shrink-0 items-center justify-between px-5 md:h-20 md:px-10'>
                        <span className='flex items-center gap-3'>
                            <DomeMark className='w-5 md:w-6' />
                            <span className='font-heading text-xl font-medium leading-none tracking-tight md:text-2xl'>
                                Unity Space
                            </span>
                        </span>

                        <button
                            type='button'
                            onClick={onClose}
                            className='group flex items-center gap-3 font-mono text-xs uppercase tracking-[0.18em]'
                        >
                            Close
                            <span className='grid size-10 place-items-center border border-tone-line transition-colors duration-500 group-hover:bg-tone-fg group-hover:text-tone'>
                                <X className='size-4' aria-hidden='true' />
                            </span>
                        </button>
                    </div>

                    <nav aria-label='Main' className='flex flex-1 flex-col justify-center px-5 py-8 md:px-10'>
                        <ul>
                            {navigation.map((item, index) => (
                                <li key={item.hash} className='border-t border-tone-line last:border-b'>
                                    <AnchorLink
                                        hash={item.hash}
                                        immediate
                                        onNavigate={onClose}
                                        data-menu-first={index === 0 ? '' : undefined}
                                        className='group flex items-center gap-4 py-3 md:gap-8 md:py-4'
                                    >
                                        <span className='w-8 font-mono text-xs text-tone-muted tabular-nums md:w-12'>
                                            {String(index + 1).padStart(2, '0')}
                                        </span>

                                        <span className='line-mask flex-1'>
                                            <motion.span
                                                className='block font-heading text-[12vw] font-light leading-[0.95] tracking-[-0.02em] transition-transform duration-700 ease-out group-hover:translate-x-4 group-hover:italic md:text-[6.4vw]'
                                                initial={{ y: reduced ? '0%' : '110%' }}
                                                animate={{ y: '0%' }}
                                                transition={{ delay: reduced ? 0 : 0.38 + index * 0.06, duration: 0.9, ease: EASE_OUT }}
                                            >
                                                {item.label}
                                            </motion.span>
                                        </span>

                                        <DomeMark
                                            variant='line'
                                            className='w-5 -translate-x-3 opacity-0 transition-all duration-500 group-hover:translate-x-0 group-hover:opacity-100 md:w-7'
                                        />
                                    </AnchorLink>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <motion.div
                        className='grid shrink-0 grid-cols-2 gap-x-6 gap-y-8 border-t border-tone-line px-5 py-8 text-sm md:grid-cols-4 md:px-10'
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: reduced ? 0 : 0.7, duration: 0.6 }}
                    >
                        <div>
                            <p className='mb-2 font-mono text-[11px] uppercase tracking-[0.18em] text-tone-muted'>Visit</p>
                            <a href={contact.mapsUrl} target='_blank' rel='noopener noreferrer' className='link-underline leading-relaxed'>
                                {contact.street}
                                <br />
                                {contact.postcode} {contact.city}
                            </a>
                        </div>

                        <div>
                            <p className='mb-2 font-mono text-[11px] uppercase tracking-[0.18em] text-tone-muted'>Write</p>
                            <a href={`mailto:${contact.email}`} className='link-underline break-all'>
                                {contact.email}
                            </a>
                        </div>

                        <div>
                            <p className='mb-2 font-mono text-[11px] uppercase tracking-[0.18em] text-tone-muted'>Community</p>
                            <a href={contact.communityUrl} target='_blank' rel='noopener noreferrer' className='link-underline'>
                                Join on Telegram
                            </a>
                        </div>

                        <div>
                            <p className='mb-2 font-mono text-[11px] uppercase tracking-[0.18em] text-tone-muted'>Host with us</p>
                            <a href={contact.bookingUrl} target='_blank' rel='noopener noreferrer' className='group inline-flex items-center gap-1 font-medium'>
                                <span className='link-underline'>Book our venue now!</span>
                                <ArrowUpRight className='size-4 transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' aria-hidden='true' />
                            </a>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}
